import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Autocomplete,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  AttachFile as AttachIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Comment as CommentIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Map as MapIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getActiveProjectId } from '../services/projectContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface ConsentType {
  id: number;
  name: string;
  code: string;
  category_id: number;
  legislation?: string;
  default_cgb?: { id: number; code: string; name: string };
  phase_durations?: {
    phase1: number;
    phase2: number;
    phase3: number;
    phase4: number;
    phase5: number;
    phase6: number;
    phase7: number;
  };
}

interface Category {
  id: number;
  name: string;
  code: string;
  color?: string;
}

interface Project {
  id: number;
  name: string;
  code: string;
}

interface Stakeholder {
  id: number;
  code: string;
  name: string;
  stakeholder_type: string;
  organization?: string;
}

interface CGB {
  id: number;
  code: string;
  name: string;
  description?: string;
}

interface ConsentForm {
  reference: string;
  consent_title: string;
  consent_type_id: number | '';
  project_id: number | '';
  cgb_id: number | '';
  status: string;
  status_percentage: number;
  criticality: number;
  
  // Location
  usrn: string;
  osmm_toid: string;
  dco_area: string;
  location_description: string;
  
  // Dates
  consent_required_date: string;
  submission_date: string;
  target_approval_date: string;
  actual_approval_date: string;
  consent_expiry_date: string;
  forecast_discharge_date: string;
  construction_start_date: string;
  
  // Planning
  float_days: number;
  scope_description: string;
  consent_requirement_assumption: string;
  legislation: string;
  
  // Activity
  project_activity: string;
  detailed_activity: string;
  linked_construction_activity: string;
  linked_construction_desc: string;
  consent_programme_p6_ref: string;
  
  // Ownership - Updated to support multiple
  primary_stakeholder_id: number | '';
  secondary_stakeholder_ids: number[];
  consent_owner: string[];
  abp_owner: string[];
  subcontractor_owner: string;
  assigned_to: string;
  reviewer: string;
  backup_user_id: number | '';
  backup_group_id: number | '';
  
  // Notes
  notes: string;
  progress_comments: string;
  drive_links: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const PHASE_NAMES = [
  'Phase 1 & 2: Initial Assessment',
  'Phase 3: Consultation',
  'Phase 4: Final Preparation',
  'Phase 5: Submit Final',
  'Phase 6: Client Approval',
  'Phase 7: CGB Approval',
  'Condition Determination',
];

const STATUS_META: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: '#94A3B8' },
  submitted: { label: 'Submitted', color: '#2450C6' },
  in_review: { label: 'In Review', color: '#7C3AED' },
  additional_info: { label: 'Additional Info', color: '#D97706' },
  approved: { label: 'Approved', color: '#16A34A' },
  rejected: { label: 'Rejected', color: '#DC2626' },
  withdrawn: { label: 'Withdrawn', color: '#6B7280' },
};

const EditConsent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dateConflict, setDateConflict] = useState<any>(null);
  const [pendingSave, setPendingSave] = useState<{ url: string; method: string; payload: any } | null>(null);
  const [dateOverrideReason, setDateOverrideReason] = useState('');
  const [loadedUpdatedAt, setLoadedUpdatedAt] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Reference data
  const [consentTypes, setConsentTypes] = useState<ConsentType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [cgbs, setCgbs] = useState<CGB[]>([]);

  // Form state
  const [form, setForm] = useState<ConsentForm>({
    reference: '',
    consent_title: '',
    consent_type_id: '',
    project_id: '',
    cgb_id: '',
    status: 'draft',
    status_percentage: 0,
    criticality: 3,
    
    usrn: '',
    osmm_toid: '',
    dco_area: '',
    location_description: '',
    
    consent_required_date: '',
    submission_date: '',
    target_approval_date: '',
    actual_approval_date: '',
    consent_expiry_date: '',
    forecast_discharge_date: '',
    construction_start_date: '',
    
    float_days: 0,
    scope_description: '',
    consent_requirement_assumption: '',
    legislation: '',
    
    project_activity: '',
    detailed_activity: '',
    linked_construction_activity: '',
    linked_construction_desc: '',
    consent_programme_p6_ref: '',
    
    primary_stakeholder_id: '',
    secondary_stakeholder_ids: [],
    consent_owner: [],
    abp_owner: [],
    subcontractor_owner: '',
    assigned_to: '',
    reviewer: '',
    backup_user_id: '',
    backup_group_id: '',
    
    notes: '',
    progress_comments: '',
    drive_links: '',
  });

  // Phases state for existing consent
  const [phases, setPhases] = useState<any[]>([]);
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [geometryPreview, setGeometryPreview] = useState<any>(null);
  const [geometryFile, setGeometryFile] = useState<File | null>(null);
  const [geometryBusy, setGeometryBusy] = useState(false);
  const [geometryMessage, setGeometryMessage] = useState('');
  const [backupUsers, setBackupUsers] = useState<any[]>([]);
  const [backupGroups, setBackupGroups] = useState<any[]>([]);

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [typeRes, catRes, projRes, shRes, cgbRes] = await Promise.all([
          fetch(`${API_BASE}/consents/consent-types`),
          fetch(`${API_BASE}/consents/categories`),
          fetch(`${API_BASE}/consents/projects`),
          fetch(`${API_BASE}/consents/stakeholders`),
          fetch(`${API_BASE}/consents/cgbs`),
        ]);

        if (typeRes.ok) setConsentTypes(await typeRes.json());
        if (catRes.ok) setCategories(await catRes.json());
        if (projRes.ok) {
          const available = await projRes.json();
          setProjects(available);
          if (isNew) {
            const active = available.find((item: Project) => item.id === getActiveProjectId());
            if (active) setForm(previous => ({ ...previous, project_id: active.id }));
          }
        }
        if (shRes.ok) setStakeholders(await shRes.json());
        if (cgbRes.ok) setCgbs(await cgbRes.json());
        const activeProject = isNew ? getActiveProjectId() : null;
        if (activeProject) {
          const backupRes = await fetch(`${API_BASE}/admin/backup/options?project_id=${activeProject}`);
          if (backupRes.ok) { const backup = await backupRes.json(); setBackupUsers(backup.users || []); setBackupGroups(backup.groups || []); }
        }
      } catch (err) {
        console.error('Error fetching reference data:', err);
      }
    };

    fetchReferenceData();
  }, []);

  // Fetch existing consent if editing
  useEffect(() => {
    if (!isNew && id) {
      const fetchConsent = async () => {
        try {
          const response = await fetch(`${API_BASE}/consents/${id}`);
          if (!response.ok) throw new Error('Consent not found');
          const data = await response.json();
          setLoadedUpdatedAt(data.updated_at || null);

          setForm({
            reference: data.reference || '',
            consent_title: data.consent_title || '',
            consent_type_id: data.consent_type?.id || '',
            project_id: data.project?.id || '',
            cgb_id: data.cgb?.id || '',
            status: data.status || 'draft',
            status_percentage: data.status_percentage || 0,
            criticality: data.criticality || 3,
            
            usrn: data.usrn || '',
            osmm_toid: data.osmm_toid || '',
            dco_area: data.dco_area || '',
            location_description: data.location_description || '',
            
            consent_required_date: data.consent_required_date || '',
            submission_date: data.submission_date || '',
            target_approval_date: data.target_approval_date || '',
            actual_approval_date: data.actual_approval_date || '',
            consent_expiry_date: data.consent_expiry_date || '',
            forecast_discharge_date: data.forecast_discharge_date || '',
            construction_start_date: data.construction_start_date || '',
            
            float_days: data.float_days || 0,
            scope_description: data.scope_description || '',
            consent_requirement_assumption: data.consent_requirement_assumption || '',
            legislation: data.legislation || '',
            
            project_activity: data.project_activity || '',
            detailed_activity: data.detailed_activity || '',
            linked_construction_activity: data.linked_construction_activity || '',
            linked_construction_desc: data.linked_construction_desc || '',
            consent_programme_p6_ref: data.consent_programme_p6_ref || '',
            
            primary_stakeholder_id: data.primary_stakeholder?.id || '',
            secondary_stakeholder_ids: data.secondary_stakeholders?.map((s: any) => s.id) || [],
            consent_owner: data.consent_owner || [],
            abp_owner: data.abp_owner || [],
            subcontractor_owner: data.subcontractor_owner || '',
            assigned_to: data.assigned_to || '',
            reviewer: data.reviewer || '',
            backup_user_id: data.backup_user?.id || '',
            backup_group_id: data.backup_group?.id || '',
            
            notes: data.notes || '',
            progress_comments: data.progress_comments || '',
            drive_links: data.drive_links || '',
          });
          
          setPhases(data.phases || []);
          setActivityLog(data.activity_log || []);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Error loading consent');
        } finally {
          setLoading(false);
        }
      };

      fetchConsent();
    }
  }, [id, isNew]);

  useEffect(() => {
    if (!form.project_id) return;
    fetch(`${API_BASE}/admin/backup/options?project_id=${form.project_id}`).then(r => r.ok ? r.json() : null)
      .then(data => { if (data) { setBackupUsers(data.users || []); setBackupGroups(data.groups || []); } }).catch(() => undefined);
  }, [form.project_id]);

  // Auto-populate CGB when consent type is selected
  useEffect(() => {
    if (form.consent_type_id && isNew) {
      const selectedType = consentTypes.find(ct => ct.id === form.consent_type_id);
      if (selectedType?.default_cgb) {
        setForm(prev => ({ ...prev, cgb_id: selectedType.default_cgb!.id }));
      }
      if (selectedType?.legislation) {
        setForm(prev => ({ ...prev, legislation: selectedType.legislation! }));
      }
    }
  }, [form.consent_type_id, consentTypes, isNew]);

  const handleChange = (field: keyof ConsentForm) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }
  ) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.consent_type_id) {
      newErrors.consent_type_id = 'Consent type is required';
    }
    if (!form.project_id) {
      newErrors.project_id = 'Project is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);
    try {
      // Flask's collection POST route is slash-terminated; keep the slash so
      // browsers do not receive a redirect response for a JSON POST.
      const url = isNew ? `${API_BASE}/consents/` : `${API_BASE}/consents/${id}`;
      const method = isNew ? 'POST' : 'PUT';

      const payload = {
        ...form,
        consent_type_id: form.consent_type_id || null,
        project_id: form.project_id || null,
        cgb_id: form.cgb_id || null,
        backup_user_id: form.backup_user_id || null,
        backup_group_id: form.backup_group_id || null,
        primary_stakeholder_id: form.primary_stakeholder_id || null,
        consent_required_date: form.consent_required_date || null,
        submission_date: form.submission_date || null,
        target_approval_date: form.target_approval_date || null,
        actual_approval_date: form.actual_approval_date || null,
        consent_expiry_date: form.consent_expiry_date || null,
        forecast_discharge_date: form.forecast_discharge_date || null,
        construction_start_date: form.construction_start_date || null,
        ...(loadedUpdatedAt ? { updated_at: loadedUpdatedAt } : {}),
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409 && errorData.error === 'date_review_required') {
          setPendingSave({ url, method, payload });
          setDateConflict(errorData);
          setSaving(false);
          return;
        } else {
          throw new Error(errorData.message || 'Failed to save consent');
        }
      }

      setSuccess(true);
      setTimeout(() => navigate('/consents'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving consent');
    } finally {
      setSaving(false);
    }
  };

  const resolveDateConflict = async (choice: 'update' | 'keep' | 'confirm' | 'cancel') => {
    if (choice === 'cancel' || !pendingSave) { setDateConflict(null); setPendingSave(null); setDateOverrideReason(''); return; }
    const payload = { ...pendingSave.payload };
    if (choice === 'update') {
      (dateConflict?.warnings || []).forEach((warning: any) => {
        if (warning.source_field && warning.target_field && payload[warning.source_field]) payload[warning.target_field] = payload[warning.source_field];
      });
      payload.date_review_decision = 'apply_proposed_related_updates';
      payload.date_review_proposed_updates = (dateConflict?.warnings || []).map((warning: any) => warning.target_field).filter(Boolean);
    } else {
      payload.date_review_decision = choice === 'keep' ? 'retain_existing_related_date' : 'confirm_changes';
      if (dateOverrideReason.trim()) payload.date_override_reason = dateOverrideReason.trim();
    }
    payload.date_review_confirmed = true;
    setSaving(true);
    try {
      const response = await fetch(pendingSave.url, { method: pendingSave.method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.message || 'Date confirmation failed'); }
      setDateConflict(null); setPendingSave(null); setDateOverrideReason(''); setSuccess(true); setTimeout(() => navigate('/consents'), 1500);
    } catch (err) { setError(err instanceof Error ? err.message : 'Date confirmation failed'); }
    finally { setSaving(false); }
  };

  const getSelectedConsentType = () => {
    return consentTypes.find(ct => ct.id === form.consent_type_id);
  };

  const getSelectedCategory = () => {
    const consentType = getSelectedConsentType();
    if (consentType) {
      return categories.find(c => c.id === consentType.category_id);
    }
    return null;
  };

  const cpOwners = stakeholders.filter(s => s.stakeholder_type === 'cp_owner');
  const abpOwners = stakeholders.filter(s => s.stakeholder_type === 'abp_owner');
  const stakeholdersList = stakeholders.filter(s => s.stakeholder_type === 'stakeholder');

  const previewGeometry = async (file: File) => {
    if (!id || file.size > 10 * 1024 * 1024) { setGeometryMessage('Choose a GeoJSON file smaller than 10 MB.'); return; }
    setGeometryBusy(true); setGeometryMessage('');
    try {
      const body = new FormData(); body.append('file', file);
      const response = await fetch(`${API_BASE}/consents/${id}/geometry?preview=true`, { method: 'POST', body });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Invalid GeoJSON');
      setGeometryFile(file); setGeometryPreview(data.details);
    } catch (error) { setGeometryPreview(null); setGeometryMessage(error instanceof Error ? error.message : 'Invalid GeoJSON'); }
    finally { setGeometryBusy(false); }
  };

  const saveGeometry = async () => {
    if (!id || !geometryFile) return;
    setGeometryBusy(true); setGeometryMessage('');
    try {
      const body = new FormData(); body.append('file', geometryFile);
      const response = await fetch(`${API_BASE}/consents/${id}/geometry`, { method: 'POST', body });
      const data = await response.json(); if (!response.ok) throw new Error(data.message || 'Unable to save geometry');
      setGeometryMessage(`Saved ${data.details.geometry_type} geometry (${data.details.feature_count} feature${data.details.feature_count === 1 ? '' : 's'}).`); setGeometryFile(null); setGeometryPreview(null);
    } catch (error) { setGeometryMessage(error instanceof Error ? error.message : 'Unable to save geometry'); }
    finally { setGeometryBusy(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Dialog open={!!dateConflict} onClose={() => resolveDateConflict('cancel')} maxWidth="sm" fullWidth>
        <DialogTitle>Review dependent dates</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Changing one date may affect another date on this consent.</Alert>
          {(dateConflict?.warnings || []).map((warning: any) => <Typography key={warning.rule_id} variant="body2" sx={{ mb: 1 }}>{warning.message}</Typography>)}
          <Typography variant="body2" color="text.secondary">Choose whether to update the dependent date or keep the difference with an explanation.</Typography>
          <TextField fullWidth size="small" label="Reason if keeping the date unchanged" value={dateOverrideReason} onChange={event => setDateOverrideReason(event.target.value)} sx={{ mt: 2 }} helperText="Required only when keeping the dates different." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resolveDateConflict('cancel')}>Cancel</Button>
          <Button onClick={() => resolveDateConflict('keep')} disabled={!!dateConflict?.review?.requires_reason && !dateOverrideReason.trim()}>Keep existing dates</Button>
          <Button onClick={() => resolveDateConflict('confirm')}>Confirm changes</Button>
          <Button variant="contained" onClick={() => resolveDateConflict('update')}>Update both dates</Button>
        </DialogActions>
      </Dialog>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/consents')}><BackIcon fontSize="small" /></IconButton>
        <Typography variant="caption" color="text.secondary">Consent Register</Typography>
      </Stack>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h4" sx={isNew ? undefined : { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {isNew ? 'Create New Consent' : (form.reference || 'Edit Consent')}
              </Typography>
              {!isNew && form.status && (
                <Chip
                  size="small"
                  label={STATUS_META[form.status]?.label || form.status}
                  sx={{ bgcolor: STATUS_META[form.status]?.color || '#6B7280', color: '#fff' }}
                />
              )}
            </Stack>
            {!isNew && form.reference ? (
              <Typography variant="body2" color="text.secondary">
                Editing consent details, phases and evidence
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Add a new regulatory consent to the register
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/consents')}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Consent'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Progress bar for existing consents */}
      {!isNew && form.status_percentage > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Overall Progress</Typography>
          <LinearProgress 
            variant="determinate" 
            value={form.status_percentage} 
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<AssignmentIcon />} label="Details" iconPosition="start" />
          <Tab icon={<MapIcon />} label="Location" iconPosition="start" />
          <Tab icon={<EventIcon />} label="Dates & Planning" iconPosition="start" />
          <Tab icon={<PersonIcon />} label="Stakeholders" iconPosition="start" />
          <Tab icon={<BusinessIcon />} label="Activity & Construction" iconPosition="start" />
          <Tab icon={<CommentIcon />} label="Notes" iconPosition="start" />
          <Tab icon={<HistoryIcon />} label={isNew ? 'Lifecycle preview' : 'Phases & History'} iconPosition="start" />
        </Tabs>
      </Paper>

      <form onSubmit={handleSubmit}>
        {/* Tab 0: Details */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Consent Details
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Consent Title"
                      value={form.consent_title}
                      onChange={handleChange('consent_title')}
                      placeholder="Enter a descriptive title for this consent"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.consent_type_id}>
                      <InputLabel>Consent Type *</InputLabel>
                      <Select
                        value={form.consent_type_id}
                        label="Consent Type *"
                        onChange={handleChange('consent_type_id')}
                      >
                        {consentTypes.map(type => (
                          <MenuItem key={type.id} value={type.id}>
                            <Box>
                              <Typography variant="body2">{type.code}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {type.name}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.consent_type_id && (
                        <Typography variant="caption" color="error">
                          {errors.consent_type_id}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Category"
                      value={getSelectedCategory()?.name || ''}
                      disabled
                      helperText="Auto-populated from consent type"
                      InputProps={{
                        startAdornment: getSelectedCategory()?.color && (
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              bgcolor: getSelectedCategory()?.color,
                              mr: 1 
                            }} 
                          />
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: form.criticality === 5 ? '#DC2626' : form.criticality === 4 ? '#D97706' : '#D6DCE6', borderRadius: 1.5, bgcolor: form.criticality === 5 ? '#FFF8F8' : 'transparent' }}>
                      <Typography variant="subtitle2" gutterBottom>Consent Criticality</Typography>
                      <ToggleButtonGroup
                        exclusive
                        value={form.criticality}
                        onChange={(_, value) => value !== null && setForm(prev => ({ ...prev, criticality: value }))}
                        aria-label="Consent criticality"
                        sx={{ flexWrap: 'wrap' }}
                      >
                        {[1, 2, 3, 4, 5].map(level => (
                          <ToggleButton key={level} value={level} aria-label={`${level} ${['Administrative', 'Low', 'Medium', 'High', 'Critical'][level - 1]}`}>
                            {level} · {['Administrative', 'Low', 'Medium', 'High', 'Critical'][level - 1]}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        5 Critical means project delivery cannot proceed without this consent.
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.project_id}>
                      <InputLabel>Project *</InputLabel>
                      <Select
                        value={form.project_id}
                        label="Project *"
                        onChange={handleChange('project_id')}
                      >
                        {projects.map(project => (
                          <MenuItem key={project.id} value={project.id}>
                            {project.name} ({project.code})
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.project_id && (
                        <Typography variant="caption" color="error">
                          {errors.project_id}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Consent Granting Body</InputLabel>
                      <Select
                        value={form.cgb_id}
                        label="Consent Granting Body"
                        onChange={handleChange('cgb_id')}
                      >
                        {cgbs.map(cgb => (
                          <MenuItem key={cgb.id} value={cgb.id}>
                            {cgb.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Reference"
                      value={form.reference}
                      onChange={handleChange('reference')}
                      helperText="Auto-generated if left blank"
                      disabled={!isNew}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Legislation"
                      value={form.legislation}
                      onChange={handleChange('legislation')}
                      helperText="Auto-populated from consent type"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Scope Description"
                      value={form.scope_description}
                      onChange={handleChange('scope_description')}
                      multiline
                      rows={4}
                      placeholder="Describe the scope and requirements of this consent..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Right Column - Status */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Status
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={form.status}
                      label="Status"
                      onChange={handleChange('status')}
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="submitted">Submitted</MenuItem>
                      <MenuItem value="in_review">In Review</MenuItem>
                      <MenuItem value="additional_info">Additional Info Required</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="withdrawn">Withdrawn</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Float Days"
                    type="number"
                    value={form.float_days}
                    onChange={handleChange('float_days')}
                    helperText="Buffer days between consent and required date"
                  />
                </Stack>
              </Paper>

              <Card sx={{ bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Quick Tips
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="CGB auto-populates from type"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="7 phases auto-created"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Holidays excluded from dates"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 1: Location */}
        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Location Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="USRN"
                  value={form.usrn}
                  onChange={handleChange('usrn')}
                  placeholder="e.g., 10000123"
                  helperText="Unique Street Reference Number"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="OSMM TOID"
                  value={form.osmm_toid}
                  onChange={handleChange('osmm_toid')}
                  placeholder="e.g., osgb1000000123456"
                  helperText="OS MasterMap reference"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="DCO Area"
                  value={form.dco_area}
                  onChange={handleChange('dco_area')}
                  helperText="Development Consent Order area"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location Description"
                  value={form.location_description}
                  onChange={handleChange('location_description')}
                  multiline
                  rows={3}
                  placeholder="Describe the physical location..."
                />
              </Grid>
              {!isNew && <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>Spatial extent</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Upload the consent boundary or location as WGS84 GeoJSON. Previous geometry versions remain in the audit history.</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <Button component="label" variant="outlined" startIcon={<AttachIcon />} disabled={geometryBusy}>
                    Choose GeoJSON
                    <input hidden type="file" accept=".json,.geojson,application/json,application/geo+json" onChange={e => { const file = e.target.files?.[0]; if (file) previewGeometry(file); e.currentTarget.value = ''; }} />
                  </Button>
                  {geometryPreview && <Button variant="contained" onClick={saveGeometry} disabled={geometryBusy}>Save geometry</Button>}
                  {geometryBusy && <CircularProgress size={20} />}
                </Stack>
                {geometryPreview && <Alert severity="info" sx={{ mt: 2 }}>Preview: {geometryPreview.geometry_type}, {geometryPreview.feature_count} feature{geometryPreview.feature_count === 1 ? '' : 's'}, CRS {geometryPreview.source_crs}. Check the preview before saving.</Alert>}
                {geometryMessage && <Typography variant="body2" color={geometryMessage.startsWith('Saved') ? 'success.main' : 'error.main'} sx={{ mt: 1 }}>{geometryMessage}</Typography>}
              </Grid>}
            </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 2: Dates & Planning */}
        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Key Dates
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Consent Required Date"
                  type="date"
                  value={form.consent_required_date}
                  onChange={handleChange('consent_required_date')}
                  InputLabelProps={{ shrink: true }}
                  helperText="When consent is needed by"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Submission Date"
                  type="date"
                  value={form.submission_date}
                  onChange={handleChange('submission_date')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Target Approval Date"
                  type="date"
                  value={form.target_approval_date}
                  onChange={handleChange('target_approval_date')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Actual Approval Date"
                  type="date"
                  value={form.actual_approval_date}
                  onChange={handleChange('actual_approval_date')}
                  InputLabelProps={{ shrink: true }}
                  disabled={!['approved'].includes(form.status)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Consent Expiry Date"
                  type="date"
                  value={form.consent_expiry_date}
                  onChange={handleChange('consent_expiry_date')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Forecast Discharge Date"
                  type="date"
                  value={form.forecast_discharge_date}
                  onChange={handleChange('forecast_discharge_date')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Planning Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <TextField
                fullWidth
                label="Consent Requirement Assumption"
                value={form.consent_requirement_assumption}
                onChange={handleChange('consent_requirement_assumption')}
                multiline
                rows={3}
                placeholder="Document assumptions about consent requirements..."
              />
            </Box>
          </Paper>
        </TabPanel>

        {/* Tab 3: Stakeholders */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Consent Ownership
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                  <Autocomplete
                    multiple
                    options={cpOwners}
                    getOptionLabel={(option) => option.name}
                    value={cpOwners.filter(o => form.consent_owner.includes(o.code))}
                    onChange={(_, newValue) => {
                      setForm(prev => ({ ...prev, consent_owner: newValue.map(v => v.code) }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Consent Owner(s)" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                  />

                  <Autocomplete
                    multiple
                    options={abpOwners}
                    getOptionLabel={(option) => option.name}
                    value={abpOwners.filter(o => form.abp_owner.includes(o.code))}
                    onChange={(_, newValue) => {
                      setForm(prev => ({ ...prev, abp_owner: newValue.map(v => v.code) }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="ABP Owner(s)" />
                    )}
                  />

                  <TextField
                    fullWidth
                    label="Subcontractor Owner"
                    value={form.subcontractor_owner}
                    onChange={handleChange('subcontractor_owner')}
                  />

                  <TextField
                    fullWidth
                    label="Assigned To"
                    value={form.assigned_to}
                    onChange={handleChange('assigned_to')}
                  />

                  <TextField
                    fullWidth
                    label="Reviewer"
                    value={form.reviewer}
                    onChange={handleChange('reviewer')}
                  />

                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle1" fontWeight={700}>Ownership coverage</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Optional fallback responsibility. Ownership is not transferred automatically.
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel>Backup person</InputLabel>
                        <Select value={form.backup_user_id} label="Backup person" onChange={handleChange('backup_user_id')}>
                          <MenuItem value="">None</MenuItem>
                          {backupUsers.map(user => <MenuItem key={user.id} value={user.id}>{user.name} ({user.availability_status})</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth>
                        <InputLabel>Backup group</InputLabel>
                        <Select value={form.backup_group_id} label="Backup group" onChange={handleChange('backup_group_id')}>
                          <MenuItem value="">None</MenuItem>
                          {backupGroups.map(group => <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Paper>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  External Stakeholders
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel>Primary Stakeholder</InputLabel>
                    <Select
                      value={form.primary_stakeholder_id}
                      label="Primary Stakeholder"
                      onChange={handleChange('primary_stakeholder_id')}
                    >
                      <MenuItem value="">None</MenuItem>
                      {stakeholdersList.map(sh => (
                        <MenuItem key={sh.id} value={sh.id}>
                          {sh.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    options={stakeholdersList}
                    getOptionLabel={(option) => option.name}
                    value={stakeholdersList.filter(s => form.secondary_stakeholder_ids.includes(s.id))}
                    onChange={(_, newValue) => {
                      setForm(prev => ({ 
                        ...prev, 
                        secondary_stakeholder_ids: newValue.map(v => v.id) 
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Secondary Stakeholders" />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                  />

                  <Alert severity="info">
                    Up to 8 secondary stakeholders can be selected per XLSForm specification.
                  </Alert>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 4: Activity & Construction */}
        <TabPanel value={tabValue} index={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Project Activity
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Project Activity"
                  value={form.project_activity}
                  onChange={handleChange('project_activity')}
                  helperText="High-level activity description"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Detailed Activity"
                  value={form.detailed_activity}
                  onChange={handleChange('detailed_activity')}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Construction Links
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Construction Start Date"
                    type="date"
                    value={form.construction_start_date}
                    onChange={handleChange('construction_start_date')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Linked Construction Activity"
                    value={form.linked_construction_activity}
                    onChange={handleChange('linked_construction_activity')}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="P6 Reference"
                    value={form.consent_programme_p6_ref}
                    onChange={handleChange('consent_programme_p6_ref')}
                    helperText="Consent programme P6 reference"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Linked Construction Description"
                    value={form.linked_construction_desc}
                    onChange={handleChange('linked_construction_desc')}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </TabPanel>

        {/* Tab 5: Notes */}
        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Internal Notes
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={form.notes}
                  onChange={handleChange('notes')}
                  placeholder="Add any internal notes..."
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Progress Comments
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  value={form.progress_comments}
                  onChange={handleChange('progress_comments')}
                  placeholder="Document progress updates..."
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Document Links
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <TextField
                  fullWidth
                  label="Drive Links"
                  value={form.drive_links}
                  onChange={handleChange('drive_links')}
                  multiline
                  rows={3}
                  placeholder="Paste links to SharePoint, Google Drive, or other document storage..."
                  helperText="One link per line"
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 6: lifecycle preview for new consents, persisted phases/history afterwards */}
        <TabPanel value={tabValue} index={6}>
          {isNew ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Lifecycle preview
                  </Typography>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    This is a read-only preview from the selected consent-type template. The phases become this consent’s saved lifecycle snapshot when you save it.
                  </Alert>
                  {getSelectedConsentType() ? (
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {getSelectedConsentType()!.code} · {getSelectedConsentType()!.name}
                      </Typography>
                      {PHASE_NAMES.map((name, index) => (
                        <Paper key={name} variant="outlined" sx={{ p: 1.75 }}>
                          <Typography fontWeight={600}>{index + 1}. {name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getSelectedConsentType()!.phase_durations?.[`phase${index + 1}` as keyof NonNullable<ConsentType['phase_durations']>] ?? 0} working days
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">Select a consent type on the Details tab to preview its lifecycle.</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>What happens next?</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Save the consent to create its lifecycle snapshot. You can then assign ownership, start phases, record progress and review the full audit history.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Consent Phases
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {phases.length > 0 ? (
                    <Stepper activeStep={phases.filter(p => p.status === 'completed').length} orientation="vertical">
                      {phases.map((phase, index) => (
                        <Step key={phase.id} completed={phase.status === 'completed'}>
                          <StepLabel
                            optional={
                              <Typography variant="caption" color="text.secondary">
                                {phase.working_days_allowed} working days
                                {phase.planned_finish_date && ` • Due: ${phase.planned_finish_date}`}
                              </Typography>
                            }
                          >
                            <Typography fontWeight={phase.status === 'in_progress' ? 600 : 400}>
                              {phase.name}
                            </Typography>
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  ) : (
                    <Alert severity={form.status === 'draft' ? 'info' : 'warning'}>
                      {form.status === 'draft'
                        ? 'Lifecycle phases are prepared from the selected consent type and scheduled when the consent is submitted.'
                        : 'This legacy consent has no lifecycle phase snapshot. A platform administrator must run the lifecycle repair before phase management can begin.'}
                    </Alert>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Activity Log
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {activityLog.length > 0 ? (
                    <List dense>
                      {activityLog.map((activity, index) => (
                        <ListItem key={activity.id || index}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <HistoryIcon fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText
                            primary={activity.description}
                            secondary={`${activity.performed_by} • ${activity.created_at}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No activity recorded yet
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </form>

      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar 
        open={success} 
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success">
          Consent saved successfully! Redirecting...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditConsent;
